import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';

import {
  Alert,
  AlertTitle,
  AlertDescription,
  Flex,
  Skeleton,
  Text,
  Icon,
} from '@kognitos/lattice';

import { VersionHistoryItem } from './VersionHistoryItem';
import { VersionChangeList } from './VersionChangeList';
import type { IVersionHistoryItemData, IVersionHistoryListItem } from './types';
import { groupItemsByDate, shouldShowBadge } from './utils';
import type { IDiffChangeSummary } from '@/shared/text-diffs/utils/diff-types';

/**
 * Props for the VersionHistoryList component.
 */
export interface IVersionHistoryListProps {
  versions: IVersionHistoryListItem[];
  selectedVersionId?: string;
  onVersionClick?: (version: IVersionHistoryItemData) => void;
  isLoading?: boolean;
  isError?: boolean;
  hasNextPage?: boolean;
  isFetchingNextPage?: boolean;
  onLoadMore?: () => void;
  /**
   * Changes the selected version introduced vs latest, rendered as sub-items
   * beneath that version. Only shown when a diff is active (empty otherwise).
   */
  changes?: IDiffChangeSummary[];
  /** Hover a change sub-item / leave (null) to preview it in the doc. */
  onChangeActivate?: (change: IDiffChangeSummary | null) => void;
  /** Click a change sub-item to scroll the doc to it. */
  onChangeSelect?: (change: IDiffChangeSummary) => void;
}

/**
 * Displays a list of version history items grouped by date.
 *
 * Organizes versions into date groups with labels like "Today", "Yesterday",
 * or specific dates (e.g., "Nov 30, 2025").
 * Versions are expected to be pre-sorted in reverse chronological order (newest first).
 *
 * @param props - Component props
 * @returns Version history list component
 *
 * @example
 * <VersionHistoryList
 *   versions={versions}
 *   selectedVersionId="v1.0"
 *   onVersionClick={handleVersionSelect}
 * />
 */
export function VersionHistoryList({
  versions,
  selectedVersionId,
  onVersionClick,
  isLoading = false,
  isError = false,
  hasNextPage = false,
  isFetchingNextPage = false,
  onLoadMore,
  changes,
  onChangeActivate,
  onChangeSelect,
}: IVersionHistoryListProps) {
  const { t } = useTranslation('automation');

  // Group items by date
  const versionGroups = useMemo(() => groupItemsByDate(versions), [versions]);

  const handleLoadMore = useCallback(() => {
    onLoadMore?.();
  }, [onLoadMore]);

  // Infinite scroll hook for pagination
  const [infiniteRef] = useInfiniteScroll({
    loading: isFetchingNextPage,
    hasNextPage: hasNextPage && !isFetchingNextPage && !isError,
    onLoadMore: handleLoadMore,
    disabled: !hasNextPage || isFetchingNextPage || isError,
  });

  // Loading state
  if (isLoading) {
    return (
      <Flex
        direction="column"
        gap={4}
        width="full"
        data-testid="version-history-list-loading"
      >
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
        <Skeleton className="h-20 w-full" />
      </Flex>
    );
  }

  // Error state
  if (isError) {
    return (
      <Alert variant="destructive">
        <Icon type="AlertCircle" />
        <Flex direction="column" gap={1} className="col-start-2">
          <AlertTitle className="text-sm">
            {t('automation.versionHistory.error.title')}
          </AlertTitle>
          <AlertDescription className="text-destructive font-normal">
            {t('automation.versionHistory.error.description')}
          </AlertDescription>
        </Flex>
      </Alert>
    );
  }

  // Empty state
  if (versions.length === 0) {
    return (
      <Flex
        direction="column"
        align="center"
        justify="center"
        gap={2}
        width="full"
        height="full"
        data-testid="version-history-list-empty"
      >
        <Text level="base" color="muted">
          {t('automation.versionHistory.emptyState')}
        </Text>
      </Flex>
    );
  }

  return (
    <Flex
      direction="column"
      gap={2}
      width="full"
      height="full"
      overflow="auto"
      data-testid="version-history-list"
    >
      {versionGroups.map(group => (
        <Flex key={group.dateKey} direction="column" gap={2} width="full">
          {/* Date group header */}
          <Text
            level="base"
            color="muted"
            weight="normal"
            data-testid="version-history-date-header"
          >
            {group.label}
          </Text>

          {/* Version items in this group. The selected version renders its
              introduced changes as sub-items directly beneath it. */}
          {group.items.map(item => {
            const isSelected = selectedVersionId === item.version;
            return (
              <Flex
                key={`${item.version}`}
                direction="column"
                gap={0}
                width="full"
                className="min-w-0"
              >
                <VersionHistoryItem
                  revisionId={item.revisionId}
                  version={item.version}
                  title={item.title}
                  author={item.author}
                  showBadge={shouldShowBadge(item, versions)}
                  stage={item.stage}
                  subtitle={item.subtitle}
                  isSelected={isSelected}
                  menuItems={item.menuItems}
                  onClick={() => onVersionClick?.(item)}
                />
                {isSelected && changes !== undefined && (
                  <VersionChangeList
                    changes={changes}
                    onChangeActivate={onChangeActivate}
                    onChangeSelect={onChangeSelect}
                  />
                )}
              </Flex>
            );
          })}
        </Flex>
      ))}

      {/* Infinite scroll sentinel and loading indicator */}
      <div ref={infiniteRef}>
        {isFetchingNextPage && (
          <Flex
            direction="column"
            gap={2}
            width="full"
            data-testid="version-history-loading-more"
          >
            <Skeleton className="h-20 w-full" />
            <Skeleton className="h-20 w-full" />
          </Flex>
        )}
      </div>
    </Flex>
  );
}
